// src/pages/ProfileAnalyzer.js
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";

import {
    Box,
    Flex,
    Input,
    Button,
    IconButton,
    VStack,
    Text,
    Heading,
    Spinner,
    Divider,
    Progress,
    HStack,
    Badge,
    CircularProgress,
    CircularProgressLabel,
    SimpleGrid,
    Card,
    CardHeader,
    CardBody,
    Stack,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";

import usePriceData from "../hooks/usePriceData";
import useWatchlist from "../hooks/useWatchlist";
import useSearchTickers from "../hooks/useSearchTickers";
import WatchlistSidebar from "../components/WatchlistSidebar";
import apiClient from "../api/apiClient";

import { Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody } from "@chakra-ui/react";

import {
    ReferenceArea,
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Layer,
} from "recharts";

const ProfileAnalyzer = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const searchInputRef = useRef(null);
    const chartContainerRef = useRef(null);

    const [hoveredTicker, setHoveredTicker] = useState(null);
    const [searchInput, setSearchInput] = useState("");

    const [selectedTicker, setSelectedTicker] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [scoreData, setScoreData] = useState(null);
    const [loadingChart, setLoadingChart] = useState(false);
    const [loadingTicker, setLoadingTicker] = useState(false);
    const [tooltipData, setTooltipData] = useState(null);
    const [chartDims, setChartDims] = useState({ width: 0, height: 0 });

    const [llmCache, setLlmCache] = useState({}); // cache for LLM outputs

    const navigate = useNavigate();
    const { watchlist, loading: loadingWatchlist, addTicker, removeTicker } = useWatchlist(navigate);
    const searchSuggestions = useSearchTickers(searchInput);
    const { priceData, lastUpdated } = usePriceData(watchlist);

    const isMobile = () => window.innerWidth < 768;

    // =============================
    // Fetch chart data (safe)
    // =============================

    function formatMarketCap(value) {
        if (value == null || isNaN(value)) return "N/A";

        if (value >= 1e9) {
            // Billions
            return `${(value / 1e9).toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}B`;
        } else if (value >= 1e6) {
            // Millions
            return `${(value / 1e6).toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}M`;
        } else {
            // Smaller numbers
            return value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
    }


    const fetchChartData = useCallback(async () => {
        if (!watchlist.length) {
            setChartData([]);
            return;
        }

        setLoadingChart(true);

        const validTickers = watchlist.filter(
            (t) => t && typeof t === "string" && t.trim() !== ""
        );
        if (!validTickers.length) {
            setChartData([]);
            return;
        }

        const jitter = (val) => {
            if (val === null || val === undefined || isNaN(val)) return 0;

            const j = (Math.random() - 0.5) * 2; // ±1
            let result = val + j;

            // Clamp the value between 0 and 100
            result = Math.max(0, Math.min(100, result));

            return parseFloat(result.toFixed(2));
        };


        const fetchSingleTicker = async (ticker, retries = 2) => {
            try {
                const res = await apiClient.post("/stock_v2/scores", { tickers: [ticker] });
                const data = res.data?.[0];
                if (!data?.llmResult) throw new Error("Invalid LLM result");



                // Extract numeric scores safely
                const growthScore = Number(data.llmResult.growth_forecast_score ?? 0);
                const riskScore = Number(data.llmResult.risk_score ?? 0);
                const valuationScore = Number(data.llmResult.valuation_score ?? 0);
                const overallScore = Number(data.llmResult.overall_score ?? 0);
                const profitabilityScore = Number(data.llmResult.profitability_score ?? 0); // NEW

                // Optional: Market cap for bubble size (if available)
                const marketCap = Number(priceData[ticker]?.marketCap / 1000000000 ?? 0);



                return {
                    ticker: data.ticker,
                    category: data.llmResult.category ?? "",
                    growth_score: jitter(growthScore),
                    risk_score: jitter(riskScore),
                    valuation_score: jitter(valuationScore),
                    profitability_score: jitter(profitabilityScore), // NEW
                    overall_score: jitter(overallScore),
                    quadrant_position: {
                        x: Math.min(100, Math.max(0, growthScore)),         // X = Growth Forecast
                        y: Math.min(100, Math.max(0, profitabilityScore)), // Y = Profitability
                    },
                    visual_encoding: {
                        bubble_size: marketCap,             // Bubble size = Market Cap
                        bubble_color: valuationScore,       // Color = Valuation score
                        bubble_border: riskScore,           // Border thickness = Risk score
                    },
                    rationale: data.llmResult.rationale ?? { summary: "", dimensions: [] },
                    status: "ok",
                };
            } catch (err) {
                if (retries > 0) {
                    console.warn(`Retrying ${ticker}... attempts left: ${retries}`);
                    await new Promise((r) => setTimeout(r, 800));
                    return fetchSingleTicker(ticker, retries - 1);
                }
                console.warn(`⚠️ Skipping ${ticker}: ${err.message}`);
                return {
                    ticker,
                    category: "N/A",
                    growth_score: 0,
                    risk_score: 0,
                    valuation_score: 0,
                    profitability_score: 0, // NEW
                    overall_score: 0,
                    quadrant_position: { x: 0, y: 0 },
                    visual_encoding: { bubble_size: 0, bubble_color: 0, bubble_border: 0 },
                    rationale: { summary: "Data unavailable", dimensions: [] },
                    status: "failed",
                };
            }
        };


        try {
            const results = await Promise.all(validTickers.map(fetchSingleTicker));
            const hasValid = results.some((r) => r.status === "ok");

            if (!hasValid) {
                toast({
                    title: "No stock scores could be loaded",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
            }

            setChartData(results);
        } catch (err) {
            console.error("Unexpected error loading chart data:", err);
        } finally {
            setLoadingChart(false);
        }
    }, [watchlist, toast]);




    // ✅ Fix #3 (optimized)
    const prevWatchlist = useRef([]);

    useEffect(() => {
        // Prevent redundant reloads when watchlist hasn’t actually changed
        if (JSON.stringify(prevWatchlist.current) === JSON.stringify(watchlist)) return;

        prevWatchlist.current = watchlist;
        fetchChartData();
    }, [fetchChartData, watchlist]);


    // =============================
    // Fetch LLM evaluation once per ticker
    // =============================
    const handleDotClick = async (ticker) => {
        setSelectedTicker(ticker);

        // If already cached, use it
        if (llmCache[ticker]) {
            setScoreData(llmCache[ticker]);
            return;
        }

        setLoadingTicker(true);
        try {
            const token = localStorage.getItem("token");
            const res = await apiClient.get(`/stock_v2/score/${ticker}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setScoreData(res.data?.llmResult);
            setLlmCache((prev) => ({ ...prev, [ticker]: res.data?.llmResult }));
        } catch (err) {
            console.error("❌ Error fetching LLM evaluation:", err);
            toast({
                title: "Failed to load evaluation",
                description: err.response?.data?.error || err.message,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoadingTicker(false);
        }
    };

    // =============================
    // ScoreData sync when selectedTicker changes
    // =============================
    useEffect(() => {
        if (!selectedTicker) return setScoreData(null);
        if (llmCache[selectedTicker]) setScoreData(llmCache[selectedTicker]);
    }, [selectedTicker, llmCache]);

    // =============================
    // Chart hover tooltip
    // =============================
    const handleHoverTicker = (ticker) => setHoveredTicker(ticker);
    const handleLeaveHover = () => setHoveredTicker(null);

    useEffect(() => {
        const activeTicker = hoveredTicker || selectedTicker;
        if (!activeTicker || chartData.length === 0 || !chartContainerRef.current) {
            setTooltipData(null);
            return;
        }

        const found = chartData.find((d) => d.ticker === activeTicker);
        if (!found) {
            setTooltipData(null);
            return;
        }

        const { clientWidth, clientHeight } = chartContainerRef.current;
        const x = (found.growth_score / 100) * clientWidth;
        const y = clientHeight - (found.risk_score / 100) * clientHeight;

        setTooltipData({
            active: true,
            payload: [{ payload: found }],
            label: found.ticker,
            coordinate: { x, y },
        });
    }, [hoveredTicker, selectedTicker, chartData]);

    // =============================
    // Add ticker
    // =============================
    const handleAddTicker = (ticker) => {
        addTicker(ticker);
        if (isMobile()) onClose();
    };

    useLayoutEffect(() => {
        if (chartContainerRef.current) {
            const { clientWidth, clientHeight } = chartContainerRef.current;
            setChartDims({ width: clientWidth, height: clientHeight });
        }
    }, [chartData]);

    const activeTicker = selectedTicker || scoreData?.ticker;
    const activeStock = chartData.find((s) => s.ticker === activeTicker);

    // =============================
    // Render
    // =============================
    // Listen for global watchlist selection events (floating button / layout drawer)
    React.useEffect(() => {
        const handler = (e) => {
            const ticker = e.detail;
            if (ticker) {
                handleDotClick(ticker);
                if (onClose) onClose();
            }
        };
        window.addEventListener('watchlist-select', handler);
        return () => window.removeEventListener('watchlist-select', handler);
    }, [handleDotClick, onClose]);
    return (
        <Flex direction={{ base: "column", md: "row" }} h="100vh">

            {/* Mobile Drawer */}
            <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerHeader>Watchlist</DrawerHeader>
                    <DrawerBody>
                        <WatchlistSidebar
                            watchlist={watchlist}
                            priceData={priceData}
                            selectedTicker={selectedTicker}
                            onSelectTicker={setSelectedTicker}
                            onRemoveTicker={removeTicker}
                            loading={loadingWatchlist}
                            lastUpdated={lastUpdated}
                            onClose={onClose}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Desktop Sidebar */}
            <Box flex="0 0 230px" overflowY="auto" display={{ base: "none", md: "block" }}>
                <WatchlistSidebar
                    watchlist={watchlist}
                    priceData={priceData}
                    selectedTicker={selectedTicker}
                    onSelectTicker={setSelectedTicker}
                    onRemoveTicker={removeTicker}
                    loading={loadingWatchlist}
                    lastUpdated={lastUpdated}
                />
            </Box>

            {/* Main Content */}
            <Flex flex="1" direction="column" px={0} py={6} overflow="hidden">
                <Box flex="1" overflowY="auto" p={0}>
                    {/* Search Box */}
                    <Box p={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                        <Box position="relative" mb={2}>
                            <Input
                                ref={searchInputRef}
                                placeholder="Search ticker..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchSuggestions.length > 0 && (
                                <Box position="absolute" top="100%" left="0" right="0" bg="white" shadow="md" borderRadius="md" zIndex={10}>
                                    <VStack spacing={0}>
                                        {searchSuggestions.map((item) => (
                                            <Flex
                                                key={item.ticker}
                                                justify="space-between"
                                                align="center"
                                                w="full"
                                                px={3}
                                                py={2}
                                                _hover={{ bg: "gray.50" }}
                                            >
                                                <Button
                                                    variant="link"
                                                    colorScheme="teal"
                                                    onClick={() => {
                                                        setSelectedTicker(item.ticker);
                                                        setSearchInput("");
                                                    }}
                                                >
                                                    {item.ticker} - {item.name}
                                                </Button>
                                                <IconButton
                                                    icon={<AddIcon />}
                                                    size="xs"
                                                    colorScheme="green"
                                                    aria-label="Add"
                                                    onClick={() => {
                                                        handleAddTicker(item.ticker);
                                                        setSearchInput("");          // Clear search input

                                                    }}
                                                />
                                            </Flex>
                                        ))}
                                    </VStack>
                                </Box>
                            )}
                        </Box>
                    </Box>


                    <Divider mb={4} />


                    {/* Loading Chart Spinner */}
                    {loadingChart && (
                        <Flex align="center" justify="center" mb={4}>
                            <Spinner size="lg" />
                            <Text ml={2}>Loading chart...</Text>
                        </Flex>
                    )}

                    {/* Chart */}
                    {/* Chart Container */}
                    <Box
                        ref={chartContainerRef}
                        w="100%"
                        h={isMobile() ? "400px" : "500px"}
                        mb={6}
                        bg="white"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        p={isMobile() ? 2 : 4}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                                margin={{
                                    top: isMobile() ? 40 : 40,
                                    right: isMobile() ? 0 : 0,
                                    bottom: isMobile() ? 40 : 40,
                                    left: isMobile() ? 0 : 0,
                                }}
                            >
                                {/* Quadrant Backgrounds */}
                                <ReferenceArea x1={0} y1={50} x2={50} y2={100} fill="#ffecec" fillOpacity={0.4} />
                                <ReferenceArea x1={50} y1={50} x2={100} y2={100} fill="#fff5e6" fillOpacity={0.4} />
                                <ReferenceArea x1={0} y1={0} x2={50} y2={50} fill="#e6f0ff" fillOpacity={0.4} />
                                <ReferenceArea x1={50} y1={0} x2={100} y2={50} fill="#e8fff0" fillOpacity={0.4} />

                                {/* Quadrant Titles */}
                                {chartDims.width > 0 && chartDims.height > 0 && (
                                    <Layer>
                                        <text
                                            x={chartDims.width * 1.35 / 4}
                                            y={chartDims.height / 4}
                                            textAnchor="middle"
                                            fontSize={isMobile() ? 10 : 12}
                                            fontWeight="600"
                                            fill="#003366"
                                        >
                                            Cash Cows
                                        </text>
                                        <text
                                            x={(chartDims.width * 3) / 4}
                                            y={chartDims.height / 4}
                                            textAnchor="middle"
                                            fontSize={isMobile() ? 10 : 12}
                                            fontWeight="600"
                                            fill="#006600"
                                        >
                                            Elite
                                        </text>
                                        <text
                                            x={chartDims.width * 1.35 / 4}
                                            y={(chartDims.height * 2.5) / 4}
                                            textAnchor="middle"
                                            fontSize={isMobile() ? 10 : 12}
                                            fontWeight="600"
                                            fill="#800000"
                                        >
                                            Starups / Troubled
                                        </text>
                                        <text
                                            x={(chartDims.width * 3) / 4}
                                            y={(chartDims.height * 2.5) / 4}
                                            textAnchor="middle"
                                            fontSize={isMobile() ? 10 : 12}
                                            fontWeight="600"
                                            fill="#cc6600"
                                        >
                                            Emerging Challengers
                                        </text>
                                    </Layer>
                                )}

                                <CartesianGrid strokeDasharray="3 3" />

                                {/* Remove numeric ticks and axis lines on mobile */}
                                <XAxis
                                    type="number"
                                    dataKey="quadrant_position.x"
                                    domain={[0, 100]}
                                    tick={true}
                                    fontSize={7}
                                    label={{
                                        value: "Growth Forecast",
                                        position: "bottom",
                                        offset: 10,
                                        fontSize: 12,
                                        fontWeight: "800",
                                    }}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="quadrant_position.y"
                                    domain={[0, 100]}
                                    tick={true}
                                    fontSize={7}
                                    label={{
                                        value: "Profitability",
                                        angle: -90,
                                        position: "leftside",
                                        fontSize: 12,
                                        fontWeight: "800",
                                    }}
                                />


                                <Tooltip
                                    cursor={{ strokeDasharray: "3 3" }}
                                    content={({ active, payload }) => {
                                        if (active && payload?.length) {
                                            const data = payload[0].payload;

                                            return (
                                                <Box p={2} bg="white" border="1px solid #ccc" borderRadius="md" shadow="md">
                                                    <Text fontWeight="bold">{data.ticker}</Text>
                                                    <Text>Growth Forecast: {data.growth_score}</Text>
                                                    <Text>Profitability: {data.profitability_score}</Text>
                                                    <Text>Market Cap: {formatMarketCap(priceData[data.ticker]?.marketCap)}</Text>
                                                    <Text>Overall Score: {data.overall_score}</Text>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />


                                {/* Scatter dots */}
                                <Scatter
                                    data={chartData}
                                    shape={({ cx, cy, payload }) => {
                                        if (cx == null || cy == null) return null;

                                        const t = payload.ticker;
                                        const isSelected = t === selectedTicker;
                                        const isHovered = t === hoveredTicker;

                                        // Bubble size from Market Cap (log scale)

                                        const marketCap = priceData[t]?.marketCap ?? 1;
                                        const radius = Math.min(40, Math.max(2, Math.log10(marketCap) - 5)); // log10 scale


                                        // Bubble color from Valuation Score
                                        const valuation = payload.visual_encoding?.valuation_score ?? payload.valuation_score ?? 50;
                                        const fillColor =
                                            valuation < 50 ? "#E74C3C" : valuation < 75 ? "#F1C40F" : "#2ECC71";

                                        // // Border width from Risk Score
                                        // const risk = payload.visual_encoding?.risk_score ?? payload.risk_score ?? 50;
                                        // const strokeWidth = Math.min(5, Math.max(1, (100 - risk) / 20)); // higher risk = thicker


                                        const strokeColor = isSelected || isHovered ? "#800cecff" : "none";
                                        return (
                                            <circle
                                                cx={cx}
                                                cy={cy}
                                                r={radius}
                                                fill={fillColor}
                                                // stroke="#0f172a"
                                                // // strokeWidth={strokeWidth}
                                                stroke={strokeColor}
                                                strokeWidth={isSelected ? 2 : isHovered ? 1.5 : 0}
                                                onMouseEnter={() => handleHoverTicker(t)}
                                                onMouseLeave={handleLeaveHover}
                                                onClick={() => handleDotClick(t)}
                                                pointerEvents="all"
                                                style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                            />
                                        );
                                    }}
                                    isAnimationActive={false}
                                />

                            </ScatterChart>
                        </ResponsiveContainer>
                    </Box>


                    <Divider mb={4} />




                    {/* Rationale Card Below Chart */}
                    {activeStock?.rationale && (


                        <Card mb={4} boxShadow="md" borderRadius="xl" overflow="hidden">
                            <CardHeader bg="gray.100" borderTopRadius="xl" py={3}>
                                <HStack justify="space-between" align="start" spacing={4}>
                                    {/* Left side: Ticker name and summary */}
                                    <Box flex="1">
                                        <Heading size="md">{activeStock.ticker}</Heading>
                                        <Text fontSize="sm" color="gray.700" noOfLines={{ base: 0, md: 4 }}>
                                            {activeStock?.rationale.summary}
                                        </Text>
                                    </Box>

                                    {/* Right side: Circular progress with score below */}
                                    <VStack spacing={1} align="center">
                                        <CircularProgress
                                            value={activeStock.overall_score}
                                            size="64px"
                                            thickness="7px"
                                            color={
                                                activeStock.overall_score >= 75
                                                    ? "green.400"
                                                    : activeStock.overall_score >= 50
                                                        ? "yellow.400"
                                                        : "red.400"
                                            }
                                        >
                                            <CircularProgressLabel fontWeight="bold" fontSize="sm">
                                                {activeStock.overall_score}
                                            </CircularProgressLabel>
                                        </CircularProgress>
                                        <Text fontSize="sm" color="gray.500" mt={0}>
                                            Overall Score
                                        </Text>
                                    </VStack>
                                </HStack>
                            </CardHeader>

                            <CardBody pt={3}>
                                {activeStock?.rationale.dimensions.map((d, i) => (
                                    <Box
                                        key={i}
                                        mb={3}
                                        p={3}
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        borderRadius="lg"
                                        bg="gray.50"
                                    >
                                        <HStack justify="space-between" mb={1}>
                                            <Text fontWeight="bold" textTransform="capitalize">
                                                {d.name.replace(/_/g, " ")}
                                            </Text>
                                            <HStack spacing={1}>
                                                {/* <Badge colorScheme="purple">{(d.weight * 100).toFixed(0)}%</Badge> */}
                                                <Badge
                                                    colorScheme={
                                                        d.score >= 75 ? "green" : d.score >= 50 ? "yellow" : "red"
                                                    }
                                                >
                                                    {d.score}
                                                </Badge>
                                            </HStack>
                                        </HStack>

                                        <Progress
                                            value={d.score}
                                            size="sm"
                                            colorScheme={
                                                d.score >= 75 ? "green" : d.score >= 50 ? "yellow" : "red"
                                            }
                                            borderRadius="md"
                                            mb={2}
                                        />

                                        <Text fontSize="sm" color="gray.600">
                                            {d.evidence}
                                        </Text>
                                    </Box>
                                ))}
                            </CardBody>
                        </Card>


                    )}

                </Box>


            </Flex>
        </Flex>
    );
};

export default ProfileAnalyzer;
